import React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  Clock,
  FileCode,
  Flame,
  Layers,
  Loader2,
  Sparkles,
  Users,
} from "lucide-react";
import {
  useCodeHealthOverview,
  useCommitHistory,
  useStaleFiles,
  useFrameworks,
  useQualityFindings,
  useSonarAnalysis,
  entitlementFromError,
  type CodeHealthOverview,
  type ChurnedFile,
  type BusFactorFile,
  type StaleFile,
  type Framework,
  type QualityFinding,
  type SonarAnalysis,
} from "../../api/code-health";
import { Card, CardContent, CardHeader, CardTitle } from "../shadcn/card";
import { Badge } from "../shadcn/badge";
import { Button } from "../shadcn/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../shadcn/table";

interface CodeHealthTabProps {
  repoId: string;
}

// ══════════════════════════════════════════════════════════════════
// Main tab
// ══════════════════════════════════════════════════════════════════

export default function CodeHealthTab({ repoId }: CodeHealthTabProps) {
  const overview = useCodeHealthOverview(repoId);

  // 402 = Pro plan required. The whole tab becomes an upsell.
  const entitlement = entitlementFromError(overview.error);
  if (entitlement?.required) {
    return <UpsellCard info={entitlement} />;
  }

  if (overview.isLoading) {
    return <LoadingState label="Loading Code Health metrics..." />;
  }

  if (overview.error) {
    return <ErrorState onRetry={() => overview.refetch()} />;
  }

  const data = overview.data;
  if (!data) return null;

  const hasData =
    (data.tiles.complexity?.top_churn_files ?? 0) > 0 ||
    (data.tiles.complexity?.open_quality_findings ?? 0) > 0 ||
    data.tiles.stale !== null ||
    data.tiles.frameworks !== null;

  if (!hasData) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      <OverviewTiles data={data} />
      <div className="grid gap-6 lg:grid-cols-2">
        <CommitHotspotsPanel repoId={repoId} />
        <StaleFilesPanel repoId={repoId} />
      </div>
      <FrameworksPanel repoId={repoId} />
      <QualityFindingsPanel repoId={repoId} />
      <SonarPanel repoId={repoId} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Overview tiles
// ══════════════════════════════════════════════════════════════════

function OverviewTiles({ data }: { data: CodeHealthOverview }) {
  const { technical_debt, complexity, stale, frameworks } = data.tiles;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Tile
        label="Technical Debt"
        icon={<Clock className="h-4 w-4" />}
        accent="warning"
        value={
          technical_debt?.sqale_minutes != null
            ? formatMinutes(technical_debt.sqale_minutes)
            : "—"
        }
        sub={
          technical_debt?.code_smells != null
            ? `${technical_debt.code_smells.toLocaleString()} code smells`
            : "Awaiting quality-metrics data"
        }
        updatedAt={technical_debt?.updated_at ?? null}
      />
      <Tile
        label="Complexity hotspots"
        icon={<Flame className="h-4 w-4" />}
        accent="critical"
        value={complexity.top_churn_files.toLocaleString()}
        sub={`${complexity.open_quality_findings.toLocaleString()} open quality findings`}
        updatedAt={complexity.updated_at}
      />
      <Tile
        label="Stale code"
        icon={<FileCode className="h-4 w-4" />}
        accent="info"
        value={
          stale?.stale_pct_files != null
            ? `${stale.stale_pct_files.toFixed(1)}%`
            : "—"
        }
        sub={
          stale?.stale_pct_loc != null
            ? `${stale.stale_pct_loc.toFixed(1)}% of LoC`
            : "No stale-file data yet"
        }
        updatedAt={stale?.updated_at ?? null}
      />
      <Tile
        label="Framework mix"
        icon={<Layers className="h-4 w-4" />}
        accent="brand"
        value={(frameworks?.framework_count ?? 0).toString()}
        sub={
          frameworks?.fragmentation_score != null
            ? `Fragmentation ${(frameworks.fragmentation_score * 100).toFixed(0)}%`
            : "Detection pending"
        }
        updatedAt={frameworks?.updated_at ?? null}
      />
    </div>
  );
}

function Tile({
  label,
  icon,
  value,
  sub,
  updatedAt,
  accent,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  sub: string;
  updatedAt: string | null;
  accent: "brand" | "critical" | "warning" | "info";
}) {
  const accentClass = {
    brand: "text-primary",
    critical: "text-red-500",
    warning: "text-amber-500",
    info: "text-sky-500",
  }[accent];

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <span className={accentClass} aria-hidden="true">
            {icon}
          </span>
        </div>
        <p className="mt-3 text-2xl font-bold tabular-nums">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
        {updatedAt && (
          <p className="mt-3 text-[10px] text-muted-foreground/80">
            Updated {relativeTime(updatedAt)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════
// Commit Hotspots panel
// ══════════════════════════════════════════════════════════════════

function CommitHotspotsPanel({ repoId }: { repoId: string }) {
  const query = useCommitHistory(repoId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Flame className="h-4 w-4 text-red-500" aria-hidden="true" />
          Commit hotspots
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <>
          {query.isLoading && <PanelSkeleton rows={5} />}
          {query.error && !query.isLoading && (
            <PanelEmpty message="Commit history not available yet. Run a baseline scan to populate." />
          )}
          {query.data && (
            <>
              <ChurnTable files={(query.data.payload.top_churned_files ?? []).slice(0, 8)} />
              <BusFactorList files={(query.data.payload.bus_factor_files ?? []).slice(0, 5)} />
            </>
          )}
        </>
      </CardContent>
    </Card>
  );
}

function ChurnTable({ files }: { files: ChurnedFile[] }) {
  if (!files.length) {
    return <PanelEmpty message="No churn data yet." />;
  }
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Top churned files
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[55%]">Path</TableHead>
            <TableHead className="text-right">Commits</TableHead>
            <TableHead className="text-right">Authors</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((f) => (
            <TableRow key={f.path}>
              <TableCell className="truncate font-mono text-xs" title={f.path}>
                {f.path}
              </TableCell>
              <TableCell className="text-right tabular-nums">{f.commits}</TableCell>
              <TableCell className="text-right tabular-nums">
                {f.unique_authors ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BusFactorList({ files }: { files: BusFactorFile[] }) {
  if (!files.length) return null;
  return (
    <div>
      <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Users className="h-3 w-3" aria-hidden="true" />
        Bus-factor risk
      </p>
      <ul className="space-y-1.5">
        {files.map((f) => (
          <li
            key={f.path}
            className="flex items-center justify-between gap-4 rounded-md border border-border/50 bg-muted/40 px-3 py-2 text-xs"
          >
            <span className="truncate font-mono" title={f.path}>
              {f.path}
            </span>
            <Badge variant="secondary" className="shrink-0">
              {f.unique_authors} author{f.unique_authors === 1 ? "" : "s"}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Stale Files panel
// ══════════════════════════════════════════════════════════════════

function StaleFilesPanel({ repoId }: { repoId: string }) {
  const query = useStaleFiles(repoId, { limit: 12 });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileCode className="h-4 w-4 text-sky-500" aria-hidden="true" />
          Stale files
        </CardTitle>
      </CardHeader>
      <CardContent>
        <>
        {query.isLoading && <PanelSkeleton rows={5} />}
        {query.error && !query.isLoading && (
          <PanelEmpty message="Stale-file analysis not available yet." />
        )}
        {query.data && (
          <>
            {query.data.summary.cutoff_date && (
              <p className="mb-3 text-xs text-muted-foreground">
                Files untouched since{" "}
                <span className="font-medium text-foreground">
                  {formatDate(query.data.summary.cutoff_date)}
                </span>{" "}
                — {query.data.summary.stale_count ?? 0} of{" "}
                {query.data.summary.total_files ?? 0} files
              </p>
            )}
            <StaleTable files={query.data.files} />
          </>
        )}
        </>
      </CardContent>
    </Card>
  );
}

function StaleTable({ files }: { files: StaleFile[] }) {
  if (!files.length) {
    return <PanelEmpty message="No stale files in this window — nice." />;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[60%]">Path</TableHead>
          <TableHead>Last touched</TableHead>
          <TableHead className="text-right">LoC</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {files.map((f) => (
          <TableRow key={f.path}>
            <TableCell className="truncate font-mono text-xs" title={f.path}>
              {f.path}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDate(f.last_modified)}
            </TableCell>
            <TableCell className="text-right tabular-nums text-xs">
              {f.loc?.toLocaleString() ?? "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ══════════════════════════════════════════════════════════════════
// Framework Mix panel
// ══════════════════════════════════════════════════════════════════

function FrameworksPanel({ repoId }: { repoId: string }) {
  const query = useFrameworks(repoId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Layers className="h-4 w-4 text-primary" aria-hidden="true" />
          Framework mix
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <>
        {query.isLoading && <PanelSkeleton rows={3} />}
        {query.error && !query.isLoading && (
          <PanelEmpty message="Framework detection pending — run a baseline scan." />
        )}
        {query.data && (
          <>
            <FrameworkBars frameworks={query.data.payload.frameworks} />
            <FragmentationBadge
              score={query.data.payload.fragmentation_score}
            />
            {query.data.payload.partial_usage_signals &&
              query.data.payload.partial_usage_signals.length > 0 && (
                <PartialUsageList
                  signals={query.data.payload.partial_usage_signals.slice(0, 5)}
                />
              )}
          </>
        )}
        </>
      </CardContent>
    </Card>
  );
}

function FrameworkBars({ frameworks }: { frameworks: Framework[] }) {
  if (!frameworks.length) {
    return <PanelEmpty message="No frameworks detected." />;
  }
  const max = Math.max(...frameworks.map((f) => f.pct ?? 0), 1);
  return (
    <div className="space-y-2">
      {frameworks.map((f) => (
        <div key={f.name}>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-medium">
              {f.name}
              {f.version_guess && (
                <span className="ml-1.5 text-muted-foreground">
                  {f.version_guess}
                </span>
              )}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {f.pct != null ? `${f.pct.toFixed(1)}%` : "—"}
              {f.loc != null && ` · ${f.loc.toLocaleString()} LoC`}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary"
              style={{ width: `${((f.pct ?? 0) / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function FragmentationBadge({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const tone =
    score < 0.3
      ? { label: "Focused", class: "bg-emerald-500/10 text-emerald-600" }
      : score < 0.6
        ? { label: "Mixed", class: "bg-amber-500/10 text-amber-600" }
        : { label: "Fragmented", class: "bg-red-500/10 text-red-600" };

  return (
    <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/40 px-3 py-2">
      <div>
        <p className="text-xs font-semibold">Fragmentation score</p>
        <p className="text-[10px] text-muted-foreground">
          How scattered framework usage is across the repo
        </p>
      </div>
      <Badge className={tone.class} variant="secondary">
        {tone.label} · {pct}%
      </Badge>
    </div>
  );
}

function PartialUsageList({
  signals,
}: {
  signals: Array<{ signal: string; file?: string; line?: number }>;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Partial-usage signals
      </p>
      <ul className="space-y-1.5 text-xs">
        {signals.map((s, i) => (
          <li
            key={`${s.signal}-${i}`}
            className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/30 px-3 py-1.5"
          >
            <AlertTriangle
              className="h-3 w-3 shrink-0 text-amber-500"
              aria-hidden="true"
            />
            <span className="font-medium">{s.signal}</span>
            {s.file && (
              <span className="truncate font-mono text-muted-foreground">
                {s.file}
                {s.line != null ? `:${s.line}` : ""}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Quality Findings table
// ══════════════════════════════════════════════════════════════════

function QualityFindingsPanel({ repoId }: { repoId: string }) {
  const query = useQualityFindings(repoId, { limit: 20 });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-primary" aria-hidden="true" />
          Quality findings
          {query.data?.pagination?.total != null && (
            <Badge variant="secondary" className="ml-2">
              {query.data.pagination.total}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <>
        {query.isLoading && <PanelSkeleton rows={6} />}
        {query.error && !query.isLoading && (
          <PanelEmpty message="Quality findings not available yet." />
        )}
        {query.data && <QualityTable findings={query.data.findings} />}
        </>
      </CardContent>
    </Card>
  );
}

function QualityTable({ findings }: { findings: QualityFinding[] }) {
  if (!findings.length) {
    return (
      <PanelEmpty message="No open quality findings — your codebase is clean on the rules checked." />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>File</TableHead>
          <TableHead className="text-right">Metric</TableHead>
          <TableHead>Severity</TableHead>
          <TableHead>Tool</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {findings.map((f) => {
          const file = f.location_data?.file_path as string | undefined;
          const line = f.location_data?.line as number | undefined;
          const metric = f.quality_metric;
          return (
            <TableRow key={f.id}>
              <TableCell className="max-w-[24rem] truncate" title={f.title}>
                {f.title}
              </TableCell>
              <TableCell
                className="max-w-[16rem] truncate font-mono text-xs text-muted-foreground"
                title={file}
              >
                {file ? `${file}${line ? `:${line}` : ""}` : "—"}
              </TableCell>
              <TableCell className="text-right tabular-nums text-xs">
                {metric?.value != null && metric?.threshold != null
                  ? `${metric.value}/${metric.threshold}`
                  : metric?.value != null
                    ? String(metric.value)
                    : "—"}
              </TableCell>
              <TableCell>
                <SeverityPill severity={f.severity} />
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {f.tool}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function SeverityPill({ severity }: { severity: string }) {
  const tone: Record<string, string> = {
    critical: "bg-red-500/15 text-red-600 border-red-500/30",
    high: "bg-orange-500/15 text-orange-600 border-orange-500/30",
    medium: "bg-amber-500/15 text-amber-600 border-amber-500/30",
    low: "bg-sky-500/15 text-sky-600 border-sky-500/30",
    info: "bg-muted text-muted-foreground border-border/50",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        tone[severity.toLowerCase()] ?? tone.info
      }`}
    >
      {severity}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════
// Quality-metrics panel
// ══════════════════════════════════════════════════════════════════

function SonarPanel({ repoId }: { repoId: string }) {
  const query = useSonarAnalysis(repoId);

  // 404 = no analysis yet. Show the "connect Sonar" hint instead of
  // an error — same rendering as before this endpoint existed.
  if (query.error) {
    return <SonarConnectHint />;
  }
  if (query.isLoading) {
    return <PanelSkeleton rows={2} />;
  }
  if (!query.data) return null;

  return <SonarMetrics analysis={query.data.analysis} />;
}

function SonarMetrics({ analysis }: { analysis: SonarAnalysis }) {
  const badge =
    analysis.status === "completed"
      ? { label: "Completed", tone: "bg-emerald-500/15 text-emerald-600" }
      : analysis.status === "failed"
        ? { label: "Failed", tone: "bg-red-500/15 text-red-600" }
        : analysis.status === "timeout"
          ? { label: "Timed out", tone: "bg-amber-500/15 text-amber-600" }
          : { label: analysis.status, tone: "bg-muted text-muted-foreground" };

  const m = analysis.metrics || {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
          Quality metrics
          <Badge variant="secondary" className={`ml-2 ${badge.tone}`}>
            {badge.label}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {analysis.status === "completed" ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <MetricCell label="Tech debt" value={formatMinutesMaybe(m["sqale_index"])} />
              <MetricCell label="Code smells" value={formatNumberMaybe(m["code_smells"])} />
              <MetricCell label="Bugs" value={formatNumberMaybe(m["bugs"])} />
              <MetricCell
                label="Vulnerabilities"
                value={formatNumberMaybe(m["vulnerabilities"])}
              />
              <MetricCell
                label="Duplications"
                value={formatPercentMaybe(m["duplicated_lines_density"])}
              />
              <MetricCell
                label="Coverage"
                value={formatPercentMaybe(m["coverage"])}
              />
              <MetricCell
                label="Complexity"
                value={formatNumberMaybe(m["complexity"])}
              />
              <MetricCell label="LoC" value={formatNumberMaybe(m["ncloc"])} />
            </div>
            <p className="text-[10px] text-muted-foreground">
              {analysis.issues_count.toLocaleString()} issues ingested · last
              analysed {relativeTime(analysis.completed_at ?? analysis.started_at)}
            </p>
          </>
        ) : (
          <p className="py-4 text-center text-xs text-muted-foreground">
            {analysis.error ?? "No metrics for this analysis."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function MetricCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/50 bg-muted/30 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-bold tabular-nums">{value}</p>
    </div>
  );
}

function SonarConnectHint() {
  return (
    <Card className="border-dashed bg-muted/20">
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            Quality-metrics integration
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            A quality-metrics analysis will run on your next baseline scan once
            the engine is connected. Tech-debt, coverage, duplications, and
            issue counts appear here.
          </p>
        </div>
        <Badge variant="secondary">Not connected</Badge>
      </CardContent>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════════
// Shared UI: upsell, empty, error, loading
// ══════════════════════════════════════════════════════════════════

function UpsellCard({
  info,
}: {
  info: { currentPlan?: string; upgradeUrl?: string; message?: string };
}) {
  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background">
      <CardContent className="flex flex-col items-start gap-5 p-8 md:flex-row md:items-center md:justify-between">
        <div className="max-w-xl space-y-3">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" aria-hidden="true" />
            <h3 className="text-lg font-bold">Code Health is a Pro feature</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {info.message ??
              "Understand where technical debt, churn, stale code, and framework fragmentation live — alongside the security findings you already track."}
          </p>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            <li className="flex items-center gap-2">
              <Flame className="h-3 w-3 text-red-500" /> Commit hotspots &
              bus-factor risk
            </li>
            <li className="flex items-center gap-2">
              <FileCode className="h-3 w-3 text-sky-500" /> Stale files by LoC
              and ownership
            </li>
            <li className="flex items-center gap-2">
              <Layers className="h-3 w-3 text-primary" /> Framework fragmentation
              score
            </li>
            <li className="flex items-center gap-2">
              <BarChart3 className="h-3 w-3 text-primary" /> PHP quality +
              static analysis findings
            </li>
          </ul>
          {info.currentPlan && (
            <p className="text-[11px] text-muted-foreground/80">
              You're currently on the{" "}
              <span className="font-semibold">{info.currentPlan}</span> plan.
            </p>
          )}
        </div>
        <Button asChild size="lg" className="gap-2">
          <Link to={info.upgradeUrl ?? "/billing"}>
            Upgrade to Pro
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
        <Activity className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-semibold">No Code Health data yet</h3>
        <p className="max-w-md text-xs text-muted-foreground">
          The first baseline scan on this repo will populate commit hotspots,
          stale files, framework mix, and quality findings. Trigger a scan from
          the top of the page.
        </p>
      </CardContent>
    </Card>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <Card className="p-12 text-center">
      <CardContent className="space-y-3 p-0">
        <Loader2
          className="mx-auto h-6 w-6 animate-spin text-primary"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="p-8 text-center">
      <CardContent className="space-y-3 p-0">
        <AlertTriangle
          className="mx-auto h-8 w-8 text-red-500"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">
          Failed to load Code Health metrics.
        </p>
        <Button size="sm" onClick={onRetry}>
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

function PanelSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-9 animate-pulse rounded-md bg-muted/60"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

function PanelEmpty({ message }: { message: string }) {
  return (
    <p className="py-6 text-center text-xs text-muted-foreground">{message}</p>
  );
}

// ══════════════════════════════════════════════════════════════════
// Formatting helpers
// ══════════════════════════════════════════════════════════════════

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 40) return `${hours}h`;
  const days = Math.round(hours / 8);
  return `${days}d`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatMinutesMaybe(v: number | string | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!isFinite(n)) return "—";
  return formatMinutes(n);
}

function formatNumberMaybe(v: number | string | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString();
}

function formatPercentMaybe(v: number | string | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

function relativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const mins = Math.round(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  } catch {
    return "";
  }
}
